'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { CategoryChips } from '@/components/CategoryChips'
import { useCart } from '@/contexts/CartContext'
import { cartTotal } from '@/lib/cart'
import { formatPrice } from '@/lib/format'
import { FREE_SHIPPING_THRESHOLD, SHIPPING_METHODS } from '@/lib/shipping'

export default function KoszykPage() {
  const { cart, removeItem, updateQty, syncCart } = useCart()
  const { items } = cart
  const total = cartTotal(items)
  const [syncNote, setSyncNote] = useState<string | null>(null)
  const syncedRef = useRef(false)

  // Koszyk żyje w localStorage — po zmianie cen w sklepie (albo usunięciu produktu)
  // był nieaktualny i checkout odrzucał go z 409 bez wyjścia. Przy wejściu na koszyk
  // weryfikujemy pozycje na serwerze: aktualizujemy ceny, usuwamy niedostępne.
  useEffect(() => {
    if (syncedRef.current || items.length === 0) return
    syncedRef.current = true
    const variantIds = items.map((i) => i.variantId)
    fetch('/api/koszyk/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantIds }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { items?: Array<{ variantId: string; available: boolean; price?: number }> } | null) => {
        if (!data?.items) return
        const updates = new Map(data.items.map((u) => [u.variantId, { available: u.available, price: u.price }]))
        const removed = items.filter((i) => updates.get(i.variantId)?.available === false)
        const repriced = items.filter((i) => {
          const u = updates.get(i.variantId)
          return u?.available && u.price !== undefined && Math.abs(u.price - i.price) > 0.001
        })
        if (removed.length > 0 || repriced.length > 0) {
          syncCart(updates)
          const parts: string[] = []
          if (repriced.length > 0) parts.push(`zaktualizowaliśmy ceny ${repriced.length} ${repriced.length === 1 ? 'pozycji' : 'pozycji'}`)
          if (removed.length > 0) parts.push(`${removed.length} ${removed.length === 1 ? 'pozycja jest już niedostępna i została usunięta' : 'pozycje są już niedostępne i zostały usunięte'}`)
          setSyncNote(`Koszyk został odświeżony: ${parts.join(', ')}.`)
        }
      })
      .catch(() => {}) // offline/błąd — koszyk działa jak dotąd, weryfikuje go API zamówienia
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (items.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center px-4 py-32 text-center"
        style={{ backgroundColor: '#07111F', minHeight: '60vh' }}
      >
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(31,184,176,0.1)' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1FB8B0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
            <path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
          </svg>
        </div>
        <h1 className="font-heading text-2xl font-bold text-white mb-3">Koszyk jest pusty</h1>
        <p className="mb-8" style={{ color: 'rgba(255,255,255,0.65)' }}>Dodaj sprzęt z katalogu, żeby zacząć.</p>
        <Link
          href="/katalog"
          className="inline-block px-8 py-3 rounded-xl font-semibold transition-opacity hover:opacity-90 mb-10"
          style={{ backgroundColor: '#FF6B47', color: '#03080F' }}
        >
          Przeglądaj katalog
        </Link>
        <div className="max-w-md">
          <CategoryChips title="Lub zacznij od" />
        </div>
      </div>
    )
  }

  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - total)
  const progress = Math.min(100, (total / FREE_SHIPPING_THRESHOLD) * 100)

  return (
    <div style={{ backgroundColor: '#07111F', minHeight: '100vh' }}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="font-heading text-3xl font-bold text-white mb-6">Twój koszyk</h1>

        {syncNote && (
          <p
            role="status"
            className="text-sm rounded-xl px-4 py-3 mb-6"
            style={{ color: '#1FB8B0', backgroundColor: 'rgba(31,184,176,0.08)', border: '1px solid rgba(31,184,176,0.25)' }}
          >
            {syncNote}
          </p>
        )}

        {/* Free shipping progress */}
        <div
          className="rounded-xl px-5 py-4 mb-8"
          style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.07)' }}
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Postęp do darmowej dostawy"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium" style={{ color: remaining === 0 ? '#34D399' : 'rgba(255,255,255,0.7)' }}>
              {remaining === 0
                ? '✓ Masz darmową dostawę!'
                : <>Brakuje Ci <span className="font-bold text-white">{formatPrice(remaining)}</span> do darmowej dostawy</>
              }
            </p>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={remaining === 0 ? '#34D399' : 'rgba(255,255,255,0.3)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progress}%`,
                backgroundColor: remaining === 0 ? '#34D399' : '#1FB8B0',
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lista produktów */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.variantId}
                className="flex gap-4 p-4 rounded-2xl"
                style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0" style={{ backgroundColor: '#0A1828' }}>
                  {item.image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={item.image} alt={item.name} width={80} height={80} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  ) : (
                    <div className="w-full h-full" style={{ backgroundColor: 'rgba(31,184,176,0.15)' }} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <Link
                    href={`/katalog/${item.categorySlug}/${item.slug}`}
                    className="font-heading font-semibold leading-snug block transition-colors hover:text-white"
                    style={{ color: 'rgba(255,255,255,0.85)' }}
                  >
                    {item.name}
                  </Link>
                  <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.variantName}</p>
                  <p className="font-bold text-white mt-1">
                    {formatPrice(item.price * item.quantity)}
                  </p>
                </div>

                <div className="flex flex-col items-end justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => removeItem(item.variantId)}
                    className="cart-remove"
                    style={{ color: 'rgba(255,255,255,0.55)' }}
                    aria-label={`Usuń ${item.name} z koszyka`}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
                    </svg>
                  </button>
                  <div
                    className="flex items-center gap-2 rounded-lg px-2 py-1"
                    style={{ border: '1px solid rgba(255,255,255,0.12)' }}
                    role="group"
                    aria-label={`Ilość produktu ${item.name}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (item.quantity > 1) updateQty(item.variantId, item.quantity - 1)
                        else removeItem(item.variantId)
                      }}
                      className="w-8 h-8 flex items-center justify-center transition-colors hover:text-white rounded"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                      aria-label={`Zmniejsz ilość ${item.name}`}
                    >
                      −
                    </button>
                    <span className="text-sm font-medium text-white w-4 text-center" aria-live="polite">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQty(item.variantId, Math.min(99, item.quantity + 1))}
                      disabled={item.quantity >= 99}
                      className="w-8 h-8 flex items-center justify-center transition-colors hover:text-white rounded disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                      aria-label={`Zwiększ ilość ${item.name}`}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Podsumowanie */}
          <div className="lg:col-span-1">
            <div
              className="rounded-2xl p-6 sticky top-24"
              style={{ backgroundColor: '#0D1E30', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <h2 className="font-heading font-semibold text-white mb-4">Podsumowanie</h2>

              <div className="space-y-2 mb-4">
                {items.map((item) => (
                  <div key={item.variantId} className="flex justify-between text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    <span className="truncate mr-2">{item.name} ×{item.quantity}</span>
                    <span className="flex-shrink-0">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="pt-4 mb-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex justify-between font-bold text-white text-lg">
                  <span>Razem</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: remaining === 0 ? '#34D399' : 'rgba(255,255,255,0.5)' }}>
                  {remaining === 0
                    ? '✓ Darmowa dostawa'
                    : `+ dostawa: paczkomat ${formatPrice(SHIPPING_METHODS.paczkomat.price)} / kurier ${formatPrice(SHIPPING_METHODS.kurier.price)}`}
                </p>
              </div>

              <Link
                href="/zamowienie"
                className="block w-full py-4 rounded-xl font-semibold text-center transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#FF6B47', color: '#03080F' }}
              >
                Przejdź do zamówienia
              </Link>

              <Link
                href="/katalog"
                className="block text-center text-sm mt-4 transition-colors hover:text-white"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                ← Kontynuuj zakupy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
